/**
 * Wizard Step Component
 *
 * Renders questions for a single wizard step
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FormField from './FormField';
import api from '../../services/api';
import './WizardStep.css';

// Business Unit → PMG → Product Family mapping (mirrors ProductForm)
const BU_PMG_HIERARCHY = {
  Analytics: {
    Cyence:           ['Cyence Accumulation', 'Cyence Health Check', 'Cyence Risk'],
    HazardHub:        ['Agent Risk View', 'HH Casualty', 'HH Permit Data', 'HH Property', 'HH Replacement Cost', 'HH Single Peril', 'HH Premium Peril'],
    'Industry Intel': ['Claims Intel'],
    Predict:          ['Predict for Claims', 'Predict for Profitability'],
    PricingCenter:    ['Pricing'],
    'AI Assistant':   ['ProNavigator'],
    'Other ADS':      ['Canvas', 'Compare', 'Spotlight', 'Weather Discrepancy Alerts'],
  },
  'Cloud Infrastructure': {
    'Platform GWCP': ['Advanced Search', 'Data Masking', 'Disaster Recovery', 'Editions', 'Guidewire Credits', 'Partner Environment', 'Read Replica', 'Snapshot Export'],
  },
  Data: {
    Data: ['Data Studio', 'DataHub', 'Explore', 'IBM Cognos', 'InfoCenter', 'Millbrook', 'SAP Data Integrator', 'Standard Reporting'],
  },
  Digital: {
    Digital: ['EnterpriseEngage', 'Guidewire for Salesforce', 'Jutro Digital Platform'],
  },
  Ecosystem: {
    Partner: ['Referral', 'Resell', 'PartnerConnect'],
  },
  InsuranceNow: {
    InsuranceNow: ['Cloud Support and Maintenance', 'Editions', 'Guidewire Credits', 'Insights', 'InsuranceNow', 'Looker'],
  },
  InsuranceSuite: {
    BillingCenter:       ['BillingCenter', 'Client Data Management', 'Cloud Data Access', 'Cloud Support and Maintenance', 'Market Package'],
    ClaimCenter:         ['Autopilot Workflow Service', 'ClaimCenter', 'Client Data Management', 'Cloud Data Access', 'Cloud Support and Maintenance', 'Coinsurance', 'Market Package', 'Multicurrency'],
    PolicyCenter:        ['Autopilot Workflow Service', 'Client Data Management', 'Cloud Data Access', 'Cloud Support and Maintenance', 'Market Package', 'PolicyCenter', 'Quoting', 'Rating', 'Reinsurance', 'Standards Based Templates', 'Submission Intake', 'Underwriting Management'],
    UnderwritingCenter:  ['UnderwritingCenter', 'Underwriting Assistant'],
    'Other Core':        ['Client Data Management', 'Cloud Support and Maintenance', 'InsuranceSuite', 'Market Package'],
  },
  'Not Applicable': {
    'Not Applicable': ['Not Applicable'],
  },
};
const ALL_PMGS = [...new Set(Object.values(BU_PMG_HIERARCHY).flatMap(m => Object.keys(m)))].sort();

const PPP_TYPE_CODES = new Set(['PPP', 'PRICING_PACKAGING']);

const WizardStep = ({
  step,
  stepTitle,
  questions,
  answers,
  requestData,
  users,
  products,
  skiReleases,
  onAnswerChange,
  onRequestDataChange,
  isEditMode,
  formType,
}) => {
  const isPPPForm = PPP_TYPE_CODES.has(formType);
  const [pppCheckMessage, setPppCheckMessage] = useState(null);
  const [productTypeFilter, setProductTypeFilter] = useState(isPPPForm ? 'COMMERCIAL' : 'ALL');

  // Basic fields for step 0 - only show if there are no questions
  const renderBasicFields = () => {
    // Only show basic fields on step 0 when there are no questions to display
    // (This handles forms without dynamic questions)
    if (step !== 0 || questions.length > 0) return null;

    return (
      <div className="wizard-step__section">
        <h3 className="wizard-step__section-title">Request Details</h3>
        <div className="wizard-step__fields">
          <FormField
            label="Product/Capability Name"
            type="text"
            value={requestData.product_name}
            onChange={(value) => onRequestDataChange('product_name', value)}
            required
            disabled={isEditMode}
            helpText={isEditMode ? "Product name cannot be changed when editing" : "Enter the name of the product or capability"}
          />
          <FormField
            label="Location"
            type="text"
            value={requestData.location}
            onChange={(value) => onRequestDataChange('location', value)}
            helpText="E.g., San Mateo, Remote - North AMER"
          />
          <FormField
            label="Work Areas"
            type="text"
            value={requestData.work_areas}
            onChange={(value) => onRequestDataChange('work_areas', value)}
            helpText="E.g., Product Management, Engineering"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="wizard-step">
      <div className="wizard-step__header">
        <h2 className="wizard-step__title">{stepTitle}</h2>
        <p className="wizard-step__description">
          Complete the fields below. Required fields are marked with an asterisk (*).
        </p>
      </div>

      {renderBasicFields()}

      {questions.length > 0 && (() => {
        // Group questions by section
        const groupedQuestions = questions.reduce((acc, question) => {
          const section = question.section || 'Questions';
          if (!acc[section]) {
            acc[section] = [];
          }
          acc[section].push(question);
          return acc;
        }, {});

        return Object.entries(groupedQuestions).map(([sectionName, sectionQuestions]) => {
          // Reorder questions: move categorization right after product_capability_name (for CUC and SalesOps forms)
          let orderedQuestions = [...sectionQuestions];
          const productIndex = orderedQuestions.findIndex(q => q.question_key === 'product_capability_name' || q.question_key === 'salesops_product_capability_name');
          const categorizationIndex = orderedQuestions.findIndex(q => q.question_key === 'categorization' || q.question_key === 'salesops_categorization');

          // Only reorder if both fields exist and categorization is NOT already right after product
          if (productIndex !== -1 && categorizationIndex !== -1) {
            // Remove categorization from its current position
            const [categorizationQ] = orderedQuestions.splice(categorizationIndex, 1);
            // Insert it right after product_capability_name (at productIndex + 1)
            orderedQuestions.splice(productIndex + 1, 0, categorizationQ);
          }

          // Reorder ski_release to be right after categorization
          const updatedCategorizationIndex = orderedQuestions.findIndex(q => q.question_key === 'categorization' || q.question_key === 'salesops_categorization');
          const skiReleaseIndex = orderedQuestions.findIndex(q => q.question_key === 'ski_release' || q.question_key === 'salesops_ski_release');

          if (updatedCategorizationIndex !== -1 && skiReleaseIndex !== -1) {
            // Remove ski_release from its current position
            const [skiReleaseQ] = orderedQuestions.splice(skiReleaseIndex, 1);
            // Insert it right after categorization (at updatedCategorizationIndex + 1)
            orderedQuestions.splice(updatedCategorizationIndex + 1, 0, skiReleaseQ);
          }

          return (
            <div key={sectionName} className="wizard-step__section">
              <h3 className="wizard-step__section-title">{sectionName}</h3>
              <div className="wizard-step__fields">
                {orderedQuestions.map((question) => {
                  // Identify field types for dynamic option population
                  const isOwnerField = question.question_key === 'owner_of_row_data' || question.question_key === 'owner' || question.question_key === 'gpr_ea_owner' || question.question_key === 'salesops_owner' || question.question_key === 'bc_owner' || question.question_key === 'gpr_ga_owner' || question.question_key === 'gpr_ex_owner' || question.question_key === 'gpr_int_owner' || question.question_key === 'gpr_mkt_owner' || question.question_key === 'product_owner' || question.question_key === 'ppp_owner' || question.question_key === 'privacy_owner' || question.question_key === 'gpr_rb_owner' || question.question_key === 'npr_owner';
                  const isProductField = question.question_key === 'ea_product_id' || question.question_key === 'gpr_rb_product_name' || question.question_key === 'gpr_ea_product_name' || question.question_key === 'product_name' || question.question_key === 'product_capability_name' || question.question_key === 'salesops_product_capability_name' || question.question_key === 'bc_product_name' || question.question_key === 'cic_product_name' || question.question_key === 'gpr_ga_product_name' || question.question_key === 'gpr_ex_product_name' || question.question_key === 'gpr_int_product_name' || question.question_key === 'gpr_mkt_product_name' || question.question_key === 'ppp_product_name' || question.question_key === 'privacy_product_name';
                  const isSkiReleaseField = question.question_key === 'ski_release' || question.question_key === 'salesops_ski_release' || question.question_key === 'bc_ski_release' || question.question_key === 'cic_ski_release' || question.question_key === 'gpr_ea_ski_release' || question.question_key === 'gpr_rb_ski_release' || question.question_key === 'gpr_ga_ski_release' || question.question_key === 'gpr_ex_ski_release' || question.question_key === 'gpr_int_ski_release' || question.question_key === 'gpr_mkt_ski_release' || question.question_key === 'ppp_ski_release' || question.question_key === 'privacy_ski_release';

                  // For owner field, use dynamic users list
                  // For product selection, use dynamic products list (filtered by type when creating)
                  // For ski_release field, use dynamic ski releases list
                  let options = question.dropdown_options;

                  // Parse JSON string options to array if needed
                  if (options && typeof options === 'string') {
                    try {
                      options = JSON.parse(options);
                    } catch (e) {
                      console.error('Error parsing dropdown options for', question.question_key, e);
                      options = [];
                    }
                  }

                  // Ensure options is an array
                  if (!Array.isArray(options)) {
                    options = options ? [options] : [];
                  }

                  // BU options always come from the hierarchy (not stale DB values)
                  if (question.question_key === 'npr_business_unit') {
                    options = Object.keys(BU_PMG_HIERARCHY);
                  }

                  // Cascade: filter PMG options by selected BU
                  if (question.question_key === 'npr_performance_matrix_group') {
                    const bu = answers['npr_business_unit'];
                    const buMap = BU_PMG_HIERARCHY[bu];
                    options = buMap ? Object.keys(buMap) : ALL_PMGS;
                  }

                  // Cascade: filter Product Family options by selected PMG
                  if (question.question_key === 'npr_product_family') {
                    const bu = answers['npr_business_unit'];
                    const pmg = answers['npr_performance_matrix_group'];
                    const buMap = BU_PMG_HIERARCHY[bu];
                    options = (buMap && pmg && buMap[pmg]) ? buMap[pmg] : [];
                  }

                  if (isOwnerField) {
                    options = users.map(user => user.displayName);
                  } else if (isProductField) {
                    const filteredProducts = (!isEditMode && productTypeFilter !== 'ALL')
                      ? products.filter(p => p.product_type === productTypeFilter)
                      : products;
                    options = filteredProducts.map(product => product.product_name);
                  } else if (isSkiReleaseField) {
                    options = skiReleases.map(skiRelease => skiRelease.full_name);
                  }

                  // Product name fields are locked in edit mode — reuse isProductField
                  const isProductNameField = isProductField;
                  const isCategorizationField = question.question_key === 'categorization' || question.question_key === 'salesops_categorization';

                  // Make categorization read-only and auto-populate from selected product
                  const isDisabled = (isEditMode && isProductNameField) || isCategorizationField;
                  let helpText = question.help_text;
                  if (isEditMode && isProductNameField) {
                    helpText = 'Product name cannot be changed when editing';
                  } else if (isCategorizationField) {
                    helpText = 'Auto-populated from selected product';
                  }

                  // Auto-populate categorization when product is selected
                  const handleChange = (value) => {
                    // For owner fields, extract just the name (without email) before saving
                    let valueToSave = value;
                    if (question.question_key === 'owner_of_row_data' || question.question_key === 'owner' || question.question_key === 'gpr_ea_owner' || question.question_key === 'salesops_owner' || question.question_key === 'bc_owner' || question.question_key === 'gpr_ga_owner' || question.question_key === 'gpr_ex_owner' || question.question_key === 'gpr_int_owner' || question.question_key === 'gpr_mkt_owner' || question.question_key === 'product_owner' || question.question_key === 'ppp_owner' || question.question_key === 'privacy_owner' || question.question_key === 'gpr_rb_owner' || question.question_key === 'npr_owner') {
                      // Find the user by displayName and use their fullName (just the name)
                      const selectedUser = users.find(u => u.displayName === value);
                      if (selectedUser) {
                        valueToSave = selectedUser.fullName;
                      }
                    }

                    onAnswerChange(question.question_key, valueToSave);

                    // BU change: clear PMG and Product Family
                    if (question.question_key === 'npr_business_unit') {
                      onAnswerChange('npr_performance_matrix_group', '');
                      onAnswerChange('npr_product_family', '');
                    }

                    // PMG change: clear Product Family
                    if (question.question_key === 'npr_performance_matrix_group') {
                      onAnswerChange('npr_product_family', '');
                    }

                    // If product_capability_name changes, auto-populate categorization
                    if (question.question_key === 'product_capability_name' || question.question_key === 'salesops_product_capability_name') {
                      const selectedProduct = products.find(p => p.product_name === value);
                      if (selectedProduct && selectedProduct.categorization) {
                        // Use the appropriate categorization key based on the form type
                        const categorizationKey = question.question_key === 'salesops_product_capability_name' ? 'salesops_categorization' : 'categorization';
                        onAnswerChange(categorizationKey, selectedProduct.categorization);
                      }
                    }

                    // PPP Apply check: when ppp_apply is set to Yes, check if PPP request exists
                    if (question.question_key === 'ppp_apply') {
                      if (value === 'Yes') {
                        const productName = answers['product_name'] || '';
                        const skiRelease = answers['ski_release'] || '';
                        api.checkPPPExists({ product_name: productName, ski_release: skiRelease })
                          .then(response => {
                            const exists = response.data.data.exists;
                            setPppCheckMessage(exists
                              ? 'A PPP request exists for this product.'
                              : 'A PPP request does not exist for this product.'
                            );
                          })
                          .catch(() => setPppCheckMessage(null));
                      } else {
                        setPppCheckMessage(null);
                      }
                    }
                  };

                  // For owner fields, convert saved value (just name) to display format (name with email)
                  let displayValue = answers[question.question_key] || '';
                  if ((question.question_key === 'owner_of_row_data' || question.question_key === 'owner' || question.question_key === 'gpr_ea_owner' || question.question_key === 'salesops_owner' || question.question_key === 'bc_owner' || question.question_key === 'gpr_ga_owner' || question.question_key === 'gpr_ex_owner' || question.question_key === 'gpr_int_owner' || question.question_key === 'gpr_mkt_owner' || question.question_key === 'product_owner' || question.question_key === 'ppp_owner' || question.question_key === 'privacy_owner' || question.question_key === 'gpr_rb_owner' || question.question_key === 'npr_owner') && displayValue) {
                    const matchingUser = users.find(u => u.fullName === displayValue);
                    if (matchingUser) {
                      displayValue = matchingUser.displayName;
                    }
                  }

                  // In edit mode, always show the current product name from the product record
                  if (isEditMode && isProductNameField) {
                    displayValue = requestData.product_name || displayValue;
                  }

                  // Override type to dropdown for dynamic fields
                  let fieldType = question.field_type;
                  if (isOwnerField || isProductField || isSkiReleaseField) {
                    fieldType = 'dropdown';
                  }

                  if (question.question_key === 'ppp_apply') {
                    return (
                      <React.Fragment key={question.question_key}>
                        <FormField
                          label={question.question_text}
                          type={fieldType}
                          value={displayValue}
                          onChange={handleChange}
                          required={question.is_required}
                          options={options}
                          helpText={helpText}
                          disabled={isDisabled}
                        />
                        {pppCheckMessage && (
                          <p className="wizard-step__ppp-message">{pppCheckMessage}</p>
                        )}
                      </React.Fragment>
                    );
                  }

                  // For product fields in create mode, show a type filter above the dropdown
                  if (isProductField && !isEditMode) {
                    return (
                      <React.Fragment key={question.question_key}>
                        <div className="wizard-step__product-filter">
                          {(isPPPForm ? ['COMMERCIAL', 'PACKAGE'] : ['ALL', 'COMMERCIAL', 'TECHNICAL', 'PACKAGE']).map(type => (
                            <button
                              key={type}
                              type="button"
                              className={`wizard-step__product-filter-btn${productTypeFilter === type ? ' wizard-step__product-filter-btn--active' : ''}`}
                              onClick={() => setProductTypeFilter(type)}
                            >
                              {type === 'ALL' ? 'All' : type === 'PACKAGE' ? 'Package' : type === 'COMMERCIAL' ? 'Commercial' : 'Technical'}
                            </button>
                          ))}
                        </div>
                        <FormField
                          label={question.question_text}
                          type={fieldType}
                          value={displayValue}
                          onChange={handleChange}
                          required={question.is_required}
                          options={options}
                          helpText={helpText}
                          disabled={isDisabled}
                        />
                      </React.Fragment>
                    );
                  }

                  return (
                    <FormField
                      key={question.question_key}
                      label={question.question_text}
                      type={fieldType}
                      value={displayValue}
                      onChange={handleChange}
                      required={question.is_required}
                      options={options}
                      helpText={helpText}
                      disabled={isDisabled}
                    />
                  );
                })}
              </div>
            </div>
          );
        });
      })()}

      {questions.length === 0 && step !== 0 && (
        <div className="wizard-step__empty">
          <p>No questions to display for this step based on your previous answers.</p>
          <p>Click "Next" to continue to the next step.</p>
        </div>
      )}
    </div>
  );
};

WizardStep.propTypes = {
  step: PropTypes.number.isRequired,
  stepTitle: PropTypes.string.isRequired,
  questions: PropTypes.array.isRequired,
  answers: PropTypes.object.isRequired,
  requestData: PropTypes.object.isRequired,
  users: PropTypes.array,
  products: PropTypes.array,
  skiReleases: PropTypes.array,
  onAnswerChange: PropTypes.func.isRequired,
  onRequestDataChange: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  formType: PropTypes.string,
};

WizardStep.defaultProps = {
  users: [],
  products: [],
  skiReleases: [],
  isEditMode: false,
  formType: null,
};

export default WizardStep;
